import React, { useMemo } from 'react';
import { ApolloProvider, ApolloClient } from '@apollo/client';
import { Router, Route } from 'react-router-dom';
import { useLocale } from 'useLocale';
import MuiPickersUtilsProvider from 'modules/muiPickers';
import MeContextProvider from 'modules/me/MeContextProvider';
import LoginPage from 'modules/login/LoginPage';
import LoadingLoginUiCircular from 'modules/login/LoadingLoginUiCircular';
import { createClient } from 'graphql/client';
import { useSessionContext, MeSessionTimeout } from 'modules/session';
import NotesSortContextProvider from 'modules/notes/NotesSortContextProvider';
import NotesFilterContextProvider from 'modules/notes/NotesFilterContextProvider';
import { MeLocaleIntlProvider, LocaleIntlProvider } from 'modules/intl';
import { OfflineScreen } from 'modules/offlineScreen';
import NotesToastContextProvider from 'modules/notes/NotesToastContextProvider';
import { CollectorRoutes } from 'modules/common/routes/CollectorRoutes';
import SmartTimesheetContextProvider from 'modules/smartDiary/smartDiaryNotes/SmartTimesheetContextProvider';
import NoteInsightsContextProvider from 'modules/Insights/Notes/NoteInsightsContextProvider';
import { DownloadPage } from 'modules/App/menu/items';
import {
  isCollectorErrorRoute,
  CollectorErrorRoutes
} from 'modules/common/routes/CollectorErrorRoutes';
import DragAndDropProvider from 'modules/dragAndDrop/DragAndDropProvider';
import CollectorSelectionContextProvider from 'modules/Collectors/contexts/CollectorSelectionContextProvide';
import SupervisorInsightContextProvider from 'modules/Insights/Notes/SupervisorInsightContextProvider';
import SmartDiaryDrawerContextProvider from 'modules/smartDiary/smartDiaryNotes/SmartDiaryList/SmartDiaryDrawerContextProvider';
import Theme from '../../Theme';
import { DrawerRoutes, Routes } from '../common';
import { useLandingPage } from '../common/hooks/useLandingPage';
import { RedirectPage } from '../common/routes/RedirectPage';
import { browserHistory } from '../../browser-history';
import { SidePanelContextProvider } from '../rootPage';
import PageLayoutContainer from './PageLayoutContainer';
import { NoLoginAccess } from './NoLoginAccess';
import { useLoginAccess } from './hooks/useLoginAccess';
import AutoPilotContextProvider from './menu/items/Settings/AutoPilotContextProvider';

function App(): JSX.Element {
  const locale = useLocale();
  const {
    isInitialized,
    hasValidToken,
    tokenRef,
    login,
    checkIsValidSession,
    provider
  } = useSessionContext();
  const client = useMemo(() => createClient(tokenRef), [tokenRef]);
  const { persistLandingPage, restoreLandingPage } = useLandingPage();
  const { hasAccess } = useLoginAccess();

  if (window.location.pathname.startsWith('/download')) {
    return (
      <ApolloProvider client={client as ApolloClient<any>}>
        <MeContextProvider>
          <Theme direction={locale.direction}>
            <MeLocaleIntlProvider>
              <Router history={browserHistory}>
                <Route path="/download" component={DownloadPage} />
              </Router>
            </MeLocaleIntlProvider>
          </Theme>
        </MeContextProvider>
      </ApolloProvider>
    );
  }

  if (!isInitialized) {
    if (!hasValidToken) {
      return (
        <Theme direction={locale.direction}>
          <LoadingLoginUiCircular />
        </Theme>
      );
    }
  }

  if (window.location.pathname === provider.callbackPath) {
    return (
      <Theme direction={locale.direction}>
        <LoadingLoginUiCircular />
      </Theme>
    );
  }

  if (isCollectorErrorRoute(window.location.pathname)) {
    return (
      <ApolloProvider client={client as ApolloClient<any>}>
        <MeContextProvider>
          <Theme direction={locale.direction}>
            <MeLocaleIntlProvider>
              <Router history={browserHistory}>
                <CollectorErrorRoutes />
              </Router>
            </MeLocaleIntlProvider>
          </Theme>
        </MeContextProvider>
      </ApolloProvider>
    );
  }

  if (!hasValidToken) {
    if (
      !window.location.pathname.toLocaleLowerCase().includes('/network-error')
    ) {
      persistLandingPage();
    }
    return (
      <Theme direction="ltr">
        <LocaleIntlProvider
          loadingComponent={<LoadingLoginUiCircular />}
          locale="en-US"
        >
          <OfflineScreen showBackdrop={false} />
          <LoginPage
            loginRedirect={login}
            checkIsValidSession={checkIsValidSession}
          />
        </LocaleIntlProvider>
      </Theme>
    );
  }

  restoreLandingPage(browserHistory);

  if (window.location.pathname.startsWith('/collector/')) {
    return (
      <ApolloProvider client={client as ApolloClient<any>}>
        <MeContextProvider>
          <Theme direction={locale.direction}>
            <MeLocaleIntlProvider>
              <Router history={browserHistory}>
                <CollectorRoutes />
              </Router>
            </MeLocaleIntlProvider>
          </Theme>
        </MeContextProvider>
      </ApolloProvider>
    );
  }

  if (window.location.pathname === '/redirect') {
    return <RedirectPage />;
  }

  if (hasAccess === false) {
    return <NoLoginAccess />;
  }

  return (
    <ApolloProvider client={client as ApolloClient<any>}>
      <MeContextProvider>
        <Theme direction={locale.direction}>
          <MeLocaleIntlProvider>
            <DragAndDropProvider>
              <MuiPickersUtilsProvider>
                <PageLayoutContainer>
                  <NotesSortContextProvider>
                    <AutoPilotContextProvider>
                      <NotesFilterContextProvider>
                        <SmartTimesheetContextProvider>
                          <NoteInsightsContextProvider>
                            <SupervisorInsightContextProvider>
                              <CollectorSelectionContextProvider>
                                <Router history={browserHistory}>
                                  <NotesToastContextProvider>
                                    <SidePanelContextProvider>
                                      <SmartDiaryDrawerContextProvider>
                                        <Routes />
                                        <DrawerRoutes />
                                      </SmartDiaryDrawerContextProvider>
                                    </SidePanelContextProvider>
                                  </NotesToastContextProvider>
                                </Router>

                                <MeSessionTimeout />
                              </CollectorSelectionContextProvider>
                            </SupervisorInsightContextProvider>
                          </NoteInsightsContextProvider>
                        </SmartTimesheetContextProvider>
                      </NotesFilterContextProvider>
                    </AutoPilotContextProvider>
                  </NotesSortContextProvider>
                </PageLayoutContainer>
              </MuiPickersUtilsProvider>
            </DragAndDropProvider>
          </MeLocaleIntlProvider>
        </Theme>
      </MeContextProvider>
    </ApolloProvider>
  );
}

export default App;
